import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  DataGrid,
  GridRow,
  type GridColDef,
  type GridRowClassNameParams,
  type GridRowProps,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { forwardRef, useMemo, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Generic server-driven DataGrid wrapper (PLAN §3.2).
 *
 * Every Phase B list page renders one of these. It standardises:
 *   - **server pagination + sort** (`paginationMode` / `sortingMode` =
 *     'server') — the parent owns the model + refetches on change.
 *   - **loading / error / empty** states.
 *   - **CSV export disabled** (PLAN §4.6) — the built-in export bypasses
 *     PII cell renderers and would leak un-redacted row data.
 *   - **no checkbox column** — bulk actions are deferred to Phase D
 *     (§4.5); every action is per-row.
 *
 * `rowCount` is the *server total* (not the page slice length) so the
 * pager renders the right page count for cursor/offset pagination.
 */
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

export interface AdminDataGridProps<TRow extends GridValidRowModel> {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  /** Total rows on the server (drives the pager). */
  rowCount: number;
  loading: boolean;
  error: Error | null;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  /** Row click → opens the detail drawer on the parent page. */
  onRowClick?: (row: TRow) => void;
  /** Override the default empty-state node. */
  emptyState?: ReactNode;
  /** Stable row id accessor (defaults to `row.id`). */
  getRowId?: (row: TRow) => string;
  /** Page-size choices; defaults to 25/50/100. */
  pageSizeOptions?: number[];
  /** `data-testid` of each row = this prefix + the row id (e2e hooks, e.g. `sm-row-`). */
  rowTestIdPrefix?: string;
  /** Extra class of a row (e.g. a hidden item greyed out); `''` — none. */
  getRowClassName?: (row: TRow) => string;
}

/** A grid row carrying `data-testid={prefix + rowId}`; built once per prefix. */
const testIdRow = (prefix: string) => {
  const TestIdRow = forwardRef<HTMLDivElement, GridRowProps>((props, ref) => (
    <GridRow ref={ref} {...props} data-testid={`${prefix}${String(props.rowId)}`} />
  ));
  TestIdRow.displayName = 'TestIdRow';
  return TestIdRow;
};

const EmptyOverlay = ({ node }: { node: ReactNode }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 4,
      }}
    >
      {node ?? (
        <Typography variant="body2" color="text.secondary">
          {t('grid.empty')}
        </Typography>
      )}
    </Box>
  );
};

export const AdminDataGrid = <TRow extends GridValidRowModel>({
  rows,
  columns,
  rowCount,
  loading,
  error,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  onRowClick,
  emptyState,
  getRowId,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowTestIdPrefix,
  getRowClassName,
}: AdminDataGridProps<TRow>): ReactElement => {
  const { t } = useTranslation();
  const rowSlot = useMemo(
    () => (rowTestIdPrefix ? testIdRow(rowTestIdPrefix) : undefined),
    [rowTestIdPrefix],
  );

  if (error) {
    return (
      <Alert severity="error" role="alert">
        {t('grid.error')} — {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="admin-data-grid">
      <DataGrid<TRow>
        rows={rows}
        columns={columns}
        {...(getRowId ? { getRowId } : {})}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        disableColumnFilter
        {...(getRowClassName
          ? {
              getRowClassName: (params: GridRowClassNameParams<TRow>) =>
                getRowClassName(params.row),
            }
          : {})}
        {...(onRowClick
          ? { onRowClick: (params: GridRowParams<TRow>) => onRowClick(params.row) }
          : {})}
        slots={{
          noRowsOverlay: () => <EmptyOverlay node={emptyState} />,
          ...(rowSlot ? { row: rowSlot } : {}),
        }}
        sx={{
          '& .MuiDataGrid-row': { cursor: onRowClick ? 'pointer' : 'default' },
          // AUT-284 — cells lay out their content, they do not clip it.
          //
          // x-data-grid v7 leaves cells as BLOCK boxes and vertically
          // centres plain text with `line-height: calc(var(--height) - 1px)`
          // — a 51px strut on a 52px row. Every INLINE child inherits that
          // strut: MUI renders `variant="caption"` as a `<span>`, so a
          // two-line identity cell (name + short id) measures 18 + 51 = 69px
          // inside 51px of cell. Centred, it overflows ~9px at each end and
          // `overflow: hidden` eats the TOP — which is why the users list
          // showed only the bottom half of a name.
          //
          // This is the grid's own `display: 'flex'` column opt-in
          // (`.MuiDataGrid-cell--flex`, and the v8 default) applied to every
          // cell: every column here defines a `renderCell`, so nothing
          // depends on the block-box behaviour it replaces. `minWidth: 0` on
          // the direct child keeps `noWrap` ellipsis working now that the
          // content is a flex item.
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'inherit',
            '& > *': { minWidth: 0 },
          },
          // Disable cell focus outline noise on a click-to-open grid.
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        }}
        autoHeight
      />
    </Box>
  );
};
